import React, { useState, useEffect } from 'react';
import { Megaphone, X, ExternalLink, ShieldAlert } from 'lucide-react';
import API_URL from '../config';

const AnnouncementsBanner = ({ user: userProp }) => {
    const [announcements, setAnnouncements] = useState([]);
    
    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const user = userProp || JSON.parse(localStorage.getItem('vjconnect_user') || '{}');
                if (!user || !user.email) return;

                // Handle both student 'department' and faculty 'work_dept'
                const dept = user.department || user.work_dept || "";

                const userQuery = new URLSearchParams({
                    email: user.email,
                    role: user.role,
                    branch: dept, // mapped to announcements.branch for students
                    year: user.year || "",
                    section: user.section || "",
                    department: dept // mapped to announcements.department for faculty
                }).toString();

                const res = await fetch(`${API_URL}/announcements?${userQuery}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.success) {
                        setAnnouncements(data.announcements);
                    }
                }
            } catch (error) {
                console.error("Error fetching announcements:", error);
            }
        };

        fetchAnnouncements();
    }, [userProp]);

    const markAsDone = async (id) => {
        // Optimistically remove from UI
        setAnnouncements(prev => prev.filter(a => a.id !== id));

        try {
            const user = JSON.parse(localStorage.getItem('vjconnect_user'));
            if (!user || !user.email) return;

            await fetch(`${API_URL}/announcements/${id}/mark-done`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_email: user.email })
            });
        } catch (error) {
            console.error("Failed to mark announcement as done", error);
        }
    };

    if (announcements.length === 0) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
            {announcements.map((ann) => (
                <div 
                    key={ann.id} 
                    className="glass-card" 
                    style={{ 
                        padding: '1.25rem', 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '0.8rem',
                        border: ann.priority === 'urgent' ? '2px solid rgba(239, 68, 68, 0.4)' : '1px solid var(--glass-border)',
                        boxShadow: ann.priority === 'urgent' ? '0 4px 20px rgba(239, 68, 68, 0.15)' : 'none',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {ann.priority === 'urgent' && (
                        <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', backgroundColor: '#ef4444' }} />
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: ann.priority === 'urgent' ? '#ef4444' : 'var(--accent-color)', fontWeight: 'bold' }}>
                            {ann.priority === 'urgent' ? <ShieldAlert size={18} /> : <Megaphone size={18} />}
                            <span>{ann.priority === 'urgent' ? 'URGENT ANNOUNCEMENT' : 'ANNOUNCEMENT'}</span>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                                by {ann.faculty_name || ann.created_by} • {new Date(ann.created_at).toLocaleDateString()}
                            </span>
                        </div>
                        <button 
                            onClick={() => markAsDone(ann.id)}
                            className="icon-btn"
                            style={{ padding: '4px', opacity: 0.7, transform: 'scale(0.9)' }}
                            title="Mark as Done"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div style={{ color: 'var(--text-primary)', fontSize: '1.05rem', lineHeight: '1.5' }}>
                        {ann.message}
                    </div>

                    {ann.link_url && (
                        <div style={{ marginTop: '0.5rem' }}>
                            <a 
                                href={ann.link_url.startsWith('http') ? ann.link_url : `https://${ann.link_url}`} 
                                target="_blank" 
                                rel="noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent-color)', textDecoration: 'none', fontWeight: 'bold', fontSize: '0.9rem' }}
                            >
                                {ann.link_title || ann.link_url} <ExternalLink size={14} />
                            </a>
                        </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                        <button 
                            onClick={() => markAsDone(ann.id)}
                            style={{ 
                                background: 'transparent', 
                                border: '1px solid var(--glass-border)', 
                                color: 'var(--text-secondary)', 
                                padding: '0.4rem 1rem', 
                                borderRadius: '20px', 
                                cursor: 'pointer', 
                                fontSize: '0.85rem',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.border = '1px solid var(--text-primary)'; }}
                            onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.border = '1px solid var(--glass-border)'; }}
                        >
                            Mark as Done
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AnnouncementsBanner;
